import classNames from 'classnames'
import { forwardRef, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useOnClickOutside from 'use-onclickoutside'
import { RoundButton } from '../../common/components/button/RoundButton'
import { Tooltip } from '../../common/components/tooltip/Tooltip'
import { HELP_MENU_CONTROL_ID, useHelpMenu } from '../../contexts/HelpMenuContextProvider'
import { useUser } from '../../contexts/userContext'
import styles from './HelpMenu.module.css'
import { HelpMenuItem } from './components/HelpMenuItem/HelpMenuItem'
import { useParams } from 'react-router-dom'
import { getClientInfo } from '../../util/browserSpecifics'

type HelpMenuProps = {
  isLegacyEditor?: boolean
  buttonClassName?: string
  shouldMirror?: boolean
  isChatOpen?: boolean
}

export const HelpMenu = forwardRef<HTMLDivElement, HelpMenuProps>(
  ({ buttonClassName, isLegacyEditor, shouldMirror, isChatOpen = false }, ref) => {
    const { showHelpMenu, toggleDisplayMenu } = useHelpMenu()
    const { user } = useUser()
    const { t } = useTranslation()
    const { gameId: gameIdParam } = useParams()

    const helpMenuRef = useRef<HTMLDivElement>(null)

    useOnClickOutside(helpMenuRef, (e) => {
      if (!(e.target as any)?.getAttribute?.('id')?.startsWith(HELP_MENU_CONTROL_ID)) {
        toggleDisplayMenu()
      }
    })

    const handleContactSupport = useCallback(() => {
      if (!window.Beacon) {
        // Beacon not available; for now just log and close menu
        console.warn('HelpScout Beacon is not available')
        toggleDisplayMenu()
        return
      }

      const clientInfo = getClientInfo()

      const identifyData: Record<string, any> = {}

      if (user?.contactName) identifyData.name = user.contactName
      if (user?.email) identifyData.email = user.email
      if (user?.activeBusiness?.id) identifyData['business-id'] = user.activeBusiness.id
      if (user?.activeBusiness?.industry) identifyData.industry = user.activeBusiness.industry
      if (gameIdParam) identifyData['game-id'] = gameIdParam
      if (user?.activeBusiness?.name) identifyData.company = user.activeBusiness.name

      // Always include client info if available
      identifyData['browser-name'] = clientInfo.browser
      identifyData['device-type'] = clientInfo.deviceType
      identifyData.platform = clientInfo.platform
      identifyData['user-agent'] = clientInfo.userAgent

      window.Beacon('identify', identifyData)

      window.Beacon('open')
      toggleDisplayMenu()
    }, [
      user?.contactName,
      user?.email,
      user?.activeBusiness?.id,
      user?.activeBusiness?.industry,
      user?.activeBusiness?.name,
      gameIdParam,
      toggleDisplayMenu,
    ])

    const handleClickSeppoGuide = useCallback(() => {
      window.open('https://seppo.io/manual/', '_blank')
      toggleDisplayMenu()
    }, [toggleDisplayMenu])

    return (
      <>
        <div
          ref={ref}
          className={classNames(
            isLegacyEditor
              ? styles.helpMenuContainerTempLegacyEditor
              : classNames(styles.helpMenuContainer, isChatOpen && styles.chatOffset),
            shouldMirror && styles.helpMenuPosMirroring,
          )}
        >
          {showHelpMenu && (
            <div ref={helpMenuRef} className={styles.helpMenu}>
              <div>
                <HelpMenuItem
                  icon={'receipt'}
                  label={t('help_menu.seppo_guide', 'Seppo Guide')}
                  onClickAction={handleClickSeppoGuide}
                />
                <HelpMenuItem
                  icon={'multipleUsers'}
                  label={t('help_menu.contact_support', 'Contact support')}
                  onClickAction={handleContactSupport}
                />
              </div>
            </div>
          )}
        </div>
        <Tooltip
          idOverride={`${HELP_MENU_CONTROL_ID}_fabButton`}
          tooltipContent={t('help_menu_button.tooltip', 'Help')}
          closeOnScroll
        >
          {(tooltipProps) => (
            <span
              className={classNames(
                isLegacyEditor
                  ? styles.fabContainerTempLegacyEditor
                  : classNames(styles.fabContainer, isChatOpen && styles.chatOffset),
                shouldMirror && styles.fabContainerPosMirroring,
              )}
            >
              <RoundButton
                icon='help'
                className={classNames(
                  isLegacyEditor ? styles.fabButtonTempLegacyEditor : styles.fabButton,
                  buttonClassName,
                )}
                onClick={toggleDisplayMenu}
                aria-label={
                  showHelpMenu
                    ? t('accessibility.help_menu_button.close', 'Close help menu')
                    : t('accessibility.help_menu_button.open', 'Open help menu')
                }
                {...tooltipProps}
              />
            </span>
          )}
        </Tooltip>
      </>
    )
  },
)
